import { Button, Col, notification, Row } from 'antd';
import { useEffect, useRef, useState } from 'react';
import styles from './index.module.scss';

import { getCatalogueById, updateCatalog } from 'shared/api/catalog';
import { Loading } from 'shared/components/Loading';
import errorHandler from 'shared/utils/errorHandler';
import CloseIcon from '../../svgs/closeIcon.svg';
import { getWSINMapFormat } from '../Catalog/constants';
import { FormLayout } from '../FormLayout';
import { InfoSlider } from '../InfoSlider/InfoSlider';
import { CatalogueFormFields } from './FormFields';
import AdminWSINActions from './AdminWSINActions';

export interface ICatalogueInfoSlider {
	onClose?: any;
	catalogueId?: any;
	reloadList?: any;
	shopType?: any;
	shouldEdit?: boolean;
	isAdminView?: any;
	companyId?: any;
}
export function CatalogueInfoSlider({
	onClose,
	catalogueId,
	reloadList,
	shopType,
	shouldEdit = true,
	isAdminView,
	companyId
}: Readonly<ICatalogueInfoSlider>) {
	const isMounted = useRef(false);
	const [loading, setLoading] = useState(false);
	const [reloadCatalogue, setReloadCatalogue] = useState(0);
	const [data, setData] = useState<any>(null);
	const [isCatalogueFormVisible, setIsCatalogueFormVisible] = useState(false);

	useEffect(() => {
		isMounted.current = true;
		return () => {
			isMounted.current = false;
		};
	}, []);
	useEffect(() => {
		fetchCatalogueDetails();
	}, [catalogueId, reloadCatalogue]);

	async function fetchCatalogueDetails() {
		try {
			setLoading(true);
			const { data } = await getCatalogueById(catalogueId, isAdminView);
			if (!isMounted.current) return;
			setData(data.catalogue);
		} catch (error) {
			if (!isMounted.current) return;
			notification.error({
				message: 'Server Error',
				description: 'Please try again after some time.',
				placement: 'topRight'
			});
			setData(null);
		} finally {
			isMounted.current && setLoading(false);
		}
	}

	const onCatalogueDetailsSubmit = async (values: any) => {
		try {
			await updateCatalog(catalogueId, {
				shopType,
				productId: data.productId,
				variantId: data.variantId,
				productTitle: values.productTitle,
				variantTitle: values.variantTitle,
				category: values.category,
				weight: values.weight,
				weightUnit: 'g',
				active: values.active,
				price: values.price,
				hsnCode: values.hsnCode,
				imageUrls: values.imageUrls,
				handle: values.handle,
				barCode: values.barCode,
				productType: values.category,
				basePrice: Number(values.basePrice),
				dimensions: {
					length: Number(values.length),
					breadth: Number(values.breadth),
					height: Number(values.height)
				},
				gstPercentage: {
					sgst: Number(values.sgst),
					cgst: Number(values.cgst),
					igst: Number(values.igst),
					utgst: Number(values.utgst || values.sgst),
					...(values.cess && { cess: Number(values.cess) })
				},
				margin: values.margin,
				d2rCategory: values.d2rCategory,
				distributorMargin: Number(values.distributorMargin)
			});
			notification.success({
				message: 'Update Product',
				description: 'updated successfully.',
				placement: 'topRight'
			});
		} catch (error) {
			errorHandler(error);
		} finally {
			setIsCatalogueFormVisible(false);
			setReloadCatalogue((prevValue) => prevValue + 1);
			reloadList();
		}
	};
	return (
		<InfoSlider onClose={onClose}>
			<div className={styles.main}>
				{loading && <Loading loading={true} />}
				<span title="Close" className="closeBtn" onClick={onClose}>
					<CloseIcon />
				</span>

				{data ? (
					<Col className={styles.catalogueInfoContainer}>
						<Row className={styles.paddedRow}>
							<Col span={24}>
								{isCatalogueFormVisible ? (
									<div className={styles.editForm}>
										<FormLayout
											formTitle={'Edit Catalog Details'}
											onSave={(values: any) => onCatalogueDetailsSubmit(values)}
											onClose={() => setIsCatalogueFormVisible(false)}
											formFields={CatalogueFormFields}
											initialValues={{
												productTitle: data?.productTitle,
												variantTitle: data?.variantTitle,
												// sku: data?.sku,
												imageUrls: data?.imageUrls,
												hsnCode: data?.hsnCode,
												category: data?.category,
												handle: data.handle,
												barcode: data?.barCode,
												active: data?.active,
												price: data?.price,
												length: data?.dimensions?.length,
												breadth: data?.dimensions?.breadth,
												height: data?.dimensions?.height,
												weight: data?.weight,
												sgst: data?.gstPercentage?.sgst,
												cgst: data?.gstPercentage?.cgst,
												igst: data?.gstPercentage?.igst,
												cess: data?.gstPercentage?.cess,
												utgst: data?.gstPercentage?.utgst,
												basePrice: data?.basePrice,
												margin: data?.margin,
												distributorMargin: data?.distributorMargin
											}}
										/>
									</div>
								) : (
									<Row className={styles.row1}>
										<Col span={24}>
											<Row>
												<Col span={18}>
													<span className="title">Product Details</span>
												</Col>
												{shouldEdit && !isAdminView && (
													<Col className={styles.icon} span={6}>
														<Button
															className={styles.button}
															onClick={(e) => {
																e.stopPropagation();
																setIsCatalogueFormVisible(true);
															}}
															type="primary"
														>
															Edit
														</Button>
														{/* <EditOutlined onClick={() => setIsCatalogueFormVisible(true)} /> */}
													</Col>
												)}
											</Row>
										</Col>
										<Col span={24} className="sub">
											<Row className="row1-row">
												<Col className={styles.col} span={11}>
													<span className="left-text">Serial No:</span>
													<span>{`${data.productId}`}</span>
												</Col>
												<Col className={styles.col} span={11}>
													<span className="left-text">Item Name:</span> <span>{data.productTitle}</span>
												</Col>
											</Row>
											<Row className="row1-row">
												<Col className={styles.col} span={11}>
													<span className="left-text">Item Variant Name:</span> <span>{data.variantTitle}</span>
												</Col>
												<Col className={styles.col} span={11}>
													<span className="left-text">Handle:</span> <span>{data.handle}</span>
												</Col>
											</Row>

											<Row className="row1-row">
												<Col className={styles.col} span={11}>
													<span className="left-text">Sku Id:</span>
													<span>{`${data.sku}`}</span>
												</Col>
												<Col className={styles.col} span={11}>
													<span className="left-text">Category:</span>
													<span>{`${data.category}`}</span>
												</Col>
											</Row>

											<Row className="row1-row">
												<Col className={styles.col} span={11}>
													<span className="left-text">Type:</span>
													<span>{`${data.productType}`}</span>
												</Col>
												<Col className={styles.col} span={11}>
													<span className="left-text">Price:</span>
													<span>{`${data.price}`}</span>
												</Col>
											</Row>

											<Row className="row1-row">
												<Col className={styles.col} span={11}>
													<span className="left-text">LxBxH (cm):</span>
													<span>{`${data?.dimensions?.length}x${data?.dimensions?.breadth}x${data?.dimensions?.height}`}</span>
												</Col>
												<Col className={styles.col} span={11}>
													<span className="left-text">Weight (gm):</span>
													<span>{`${data.weight}`}</span>
												</Col>
											</Row>

											<Row className="row1-row">
												<Col className={styles.col} span={11}>
													<span className="left-text">Stock:</span>
													<span>{`${data.inventoryQuantity}`}</span>
												</Col>
												<Col className={styles.col} span={11}>
													<span className="left-text">Status:</span>
													<span>{`${data.active ? 'Active' : 'InActive'}`}</span>
												</Col>
											</Row>
											<Row className="row1-row">
												<Col className={styles.col} span={11}>
													<span className="left-text">HSN Code:</span>
													<span>{data.hsnCode}</span>
												</Col>
												<Col className={styles.col} span={11}>
													<span className="left-text">WSIN Map:</span>
													<span>{`${data.wsinMapping ? getWSINMapFormat(data.wsinMapping) : '-'}`}</span>
												</Col>
											</Row>
											<Row className="row1-row">
												<Col className={styles.col} span={11}>
													<span className="left-text">Margin:</span>
													<span>{data.margin ? data.margin : '-'}</span>
												</Col>
												<Col className={styles.col} span={11}>
													<span className="left-text">Distributor Margin:</span>
													<span>{`${data.distributorMargin ? data.distributorMargin : '-'}`}</span>
												</Col>
											</Row>
											<Row className="row1-row">
												<Col className={styles.col} span={11}>
													<span className="left-text">Base Price:</span>
													<span>{data.basePrice ? data.basePrice : '-'}</span>
												</Col>
												<Col className={styles.col} span={11}>
													<span className="left-text">Barcode:</span> <span>{data.barCode || ''}</span>
												</Col>
											</Row>
											<Row className="row1-row">
												<Col className={styles.col} span={11}>
													<span className="left-text">Cess %</span>
													<span>{data.gstPercentage.cess ? data.gstPercentage.cess : '-'}</span>
												</Col>
											</Row>
											<AdminWSINActions
												productId={data.id}
												wsinMapping={data.wsinMapping}
												companyId={companyId}
												isAdminView={isAdminView}
												setReloadCatalogue={setReloadCatalogue}
												reloadList={reloadList}
											/>
										</Col>
									</Row>
								)}
							</Col>
						</Row>
					</Col>
				) : (
					!loading && <div className="noDataDefaultComponent">Nothing to do here</div>
				)}
			</div>
		</InfoSlider>
	);
}
